/** Keep shared tooltips moving even when ApexCharts skips an empty bar series. */
export function followSharedTooltipCursor(
  event: MouseEvent,
  chart: {
    el: HTMLElement;
    graphics: {
      getMarkerPath: (
        x: number,
        y: number,
        shape: string,
        size: number,
      ) => string;
    };
    w: {
      globals: {
        tooltip?: {
          tooltipUtil: { getHoverMarkerSize: (index: number) => number };
        };
        collapsedSeriesIndices: number[];
        capturedDataPointIndex: number;
        pointsArray: Array<Array<[number, number | null]> | undefined>;
        gridHeight: number;
      };
    };
  },
): void {
  const { clientX, clientY } = event;
  if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;

  // ApexCharts updates tooltip content and visibility later in the same event.
  queueMicrotask(() => {
    const canvas = chart.el.querySelector<HTMLElement>(".apexcharts-canvas");
    const tooltip = canvas?.querySelector<HTMLElement>(
      ".apexcharts-tooltip.apexcharts-active",
    );
    if (!canvas || !tooltip) return;
    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const x = (clientX - bounds.left) * (canvas.offsetWidth / bounds.width);
    const y = (clientY - bounds.top) * (canvas.offsetHeight / bounds.height);
    // Keep the hover points aligned with the date shown in the tooltip.
    const globals = chart.w.globals;
    const index = globals.capturedDataPointIndex;
    // Address markers by series identity: empty columns can disrupt ApexCharts'
    // positional marker lookup. Reuse its paths, colors, and mouseout cleanup.
    canvas
      .querySelectorAll<SVGGElement>(".apexcharts-series-markers-wrap")
      .forEach((wrap) => {
        const seriesIndex = Number(wrap.getAttribute("data:realIndex"));
        const point = globals.pointsArray[seriesIndex]?.[index];
        const markers = wrap.querySelectorAll<SVGElement>(".apexcharts-marker");
        const marker = markers.item(markers.length - 1);
        if (!marker) return;
        if (
          globals.collapsedSeriesIndices.includes(seriesIndex) ||
          !point ||
          !Number.isFinite(point[0]) ||
          point[1] === null ||
          !Number.isFinite(point[1]) ||
          point[1] < 0 ||
          point[1] > globals.gridHeight
        ) {
          marker.setAttribute("d", "M0,0");
          return;
        }
        const size =
          globals.tooltip?.tooltipUtil.getHoverMarkerSize(seriesIndex) ?? 4;
        marker.setAttribute("cx", String(point[0]));
        marker.setAttribute("cy", String(point[1]));
        marker.setAttribute(
          "d",
          chart.graphics.getMarkerPath(
            point[0],
            point[1],
            marker.getAttribute("shape") ?? "circle",
            size,
          ),
        );
      });
    const width = tooltip.offsetWidth;
    const height = tooltip.offsetHeight;
    const gap = 12;
    const left =
      x + width + gap > canvas.offsetWidth ? x - width - gap : x + gap;
    const top =
      y + height + gap > canvas.offsetHeight ? y - height - gap : y + gap;
    tooltip.style.left = `${Math.max(0, Math.min(left, canvas.offsetWidth - width))}px`;
    tooltip.style.top = `${Math.max(0, Math.min(top, canvas.offsetHeight - height))}px`;
  });
}
